import { Buffer } from 'buffer';
import { buf as crc32buf } from 'crc-32';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useRole } from '../hooks';
import { Validity } from '../services/users_v1';

export const RESTRICTED_TVTHEMES = ['erotic'];

export enum ROLE {
    UNKNOWN = 'unknown',
    PARTICIPANT = 'participant',
    PARTNER_OWNER = 'partner_owner',
    PARTNER_ADMIN = 'partner_administrator',
    SITE_ADMIN = 'site_administrator',
    SITE_EDITOR = 'site_editor',
    SITE_NEWS_EDITOR = 'site_news_editor',
    SITE_MODERATOR = 'site_moderator',
    SITE_AUDITOR = 'site_auditor',
    PROVIDER_OWNER = 'provider_owner',
    PROVIDER_ADMIN = 'provider_administrator',
    PROVIDER_CONTENT_MANAGER = 'provider_content_manager',
    PROVIDER_AUDITOR = 'provider_auditor',
    PROVIDER_ENGINEER = 'provider_engineer',
    PROVIDER_STORAGE_OPERATOR = 'provider_storage_operator',
    CONSUMER_OWNER = 'consumer_owner',
    CONSUMER_ADMIN = 'consumer_administrator',
    CONSUMER_CONTENT_MANAGER = 'consumer_content_manager',
    CONSUMER_AUDITOR = 'consumer_auditor',
    CONSUMER_ENGINEER = 'consumer_engineer',
    CONSUMER_STORAGE_OPERATOR = 'consumer_storage_operator',
    PARTNER_MODERATOR_OWNER = 'partner_moderator_owner',
    PARTNER_MODERATOR_ADMIN = 'partner_moderator_administrator',
    PARTNER_MODERATOR_MODERATOR = 'partner_moderator_moderator',
    PARTNER_STORAGE_OPERATOR = 'partner_storage_operator',
    PARTNER_TVSRC_OWNER = 'partner_tvsrc_owner',
    PARTNER_TVSRC_ADMIN = 'partner_tvsrc_administrator',
    PARTNER_TVSRC_CONTENT_MANAGER = 'partner_tvsrc_content_manager',
    PARTNER_TVSRC_MDLG_OWNER = 'partner_tvsrc_mdlg_owner',
    PARTNER_TVSRC_MDLG_ADMIN = 'partner_tvsrc_mdlg_administrator',
    PARTNER_TVSRC_MDLG_CONTENT_MANAGER = 'partner_tvsrc_mdlg_content_manager',
    GUEST_GUEST = 'guest_guest',
}

export enum HISTORY_ROLE {
    CONSUMER = 'consumer',
    PROVIDER = 'provider',
    ADMIN = 'administrator',
    MODERATOR = 'moderator',
    UNKNOWN = 'unknown'
}

export function isConsumer(role: ROLE) {
    const result = (
        role === ROLE.CONSUMER_OWNER
        || role === ROLE.CONSUMER_ADMIN
        || role === ROLE.CONSUMER_AUDITOR
        || role === ROLE.CONSUMER_CONTENT_MANAGER
        || role === ROLE.CONSUMER_ENGINEER
        || role === ROLE.PARTNER_TVSRC_OWNER
        || role === ROLE.PARTNER_TVSRC_ADMIN
        || role === ROLE.PARTNER_TVSRC_MDLG_OWNER
        || role === ROLE.PARTNER_TVSRC_MDLG_ADMIN
    )
    return result;
}

export function isProvider(role: ROLE) {
    const result = (role === ROLE.PROVIDER_OWNER || role === ROLE.PROVIDER_ADMIN
        || role === ROLE.PROVIDER_AUDITOR || role === ROLE.PROVIDER_CONTENT_MANAGER
        || role === ROLE.PROVIDER_ENGINEER)
    return result;
}

export function useIsConsumer() {
    const role = useRole();

    return useMemo(() => [ROLE.CONSUMER_OWNER, ROLE.CONSUMER_AUDITOR, ROLE.CONSUMER_CONTENT_MANAGER, ROLE.CONSUMER_ENGINEER, ROLE.CONSUMER_ADMIN].includes(role.label), [role]);
}

export function useIsProvider() {
    const role = useRole();

    return useMemo(() => [ROLE.PROVIDER_ADMIN, ROLE.PROVIDER_AUDITOR, ROLE.PROVIDER_CONTENT_MANAGER, ROLE.PROVIDER_ENGINEER, ROLE.PROVIDER_OWNER].includes(role.label), [role]);
}

export function useIsAdmin() {
    const role = useRole();

    return useMemo(() => role.label === ROLE.SITE_ADMIN, [role]);
}

export function useIsSiteEditor() {
    const role = useRole();

    return useMemo(() => role.label === ROLE.SITE_EDITOR, [role]);
}

export function useIsSiteNewsEditor() {
    const role = useRole();

    return useMemo(() => role.label === ROLE.SITE_NEWS_EDITOR, [role]);
}

export function useIsGuest() {
    const role = useRole();

    return useMemo(() => role.label === ROLE.GUEST_GUEST, [role]);
}

export function useIsOperatorStorage() {
    const role = useRole();

    return useMemo(() => [ROLE.PROVIDER_STORAGE_OPERATOR, ROLE.CONSUMER_STORAGE_OPERATOR, ROLE.PARTNER_STORAGE_OPERATOR].includes(role.label), [role]);
}

export function useIsPartnerSource() {
    const role = useRole();

    return useMemo(() => [ROLE.PARTNER_TVSRC_OWNER, ROLE.PARTNER_TVSRC_ADMIN, ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN].includes(role.label), [role]);
}

export function useIsMedialogistic() {
    const role = useRole();

    return useMemo(() => [ROLE.PARTNER_TVSRC_MDLG_OWNER, ROLE.PARTNER_TVSRC_MDLG_ADMIN].includes(role.label), [role]);
}

export function useIsUserManager() {
    const role = useRole();

    return (
        role.label === ROLE.SITE_ADMIN
        // role.label === ROLE.PARTNER_OWNER ||
        // role.label === ROLE.PARTNER_MODERATOR_OWNER ||
        // role.label === ROLE.PARTNER_MODERATOR_ADMIN ||
        // role.label === ROLE.PARTNER_TVSRC_MDLG_OWNER ||
        // role.label === ROLE.PARTNER_TVSRC_ADMIN ||
        // role.label === ROLE.PROVIDER_OWNER || role.label === ROLE.PROVIDER_ADMIN ||
        // role.label === ROLE.CONSUMER_OWNER || role.label === ROLE.CONSUMER_ADMIN
    );
}

export function useIsCanModerate() {
    const role = useRole();

    return useMemo(() => [ROLE.SITE_ADMIN, ROLE.SITE_MODERATOR].includes(role.label), [role]);
}

export function useIsModerator() {
    const role = useRole();

    return useMemo(() => [ROLE.SITE_MODERATOR].includes(role.label), [role]);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function _toString(value: any, keySeparator?: string): string {
    const sep = keySeparator || '\n';
    if (typeof value === 'number') {
        return value.toString();
    }
    if (typeof value === 'string') {
        return t(value);
    }
    if (Array.isArray(value)) {
        return value.map(v => _toString(v, sep)).join(' ');
    }
    if (typeof value === 'object') {
        return Object.keys(value).map((v) => {
            if (v === 'non_field_errors') {
                return _toString(value[v]);
            } else {
                return `${v}: ${_toString(value[v], sep)}`
            }
        }).join(sep);
    }
    return value?.toString();
}

/**
 * Функция приведения ошибки к человекочитаемому представлению
 * @func extractErrorDetail
 *
 * @param {any}  e   объект ошибки от сервера или XMLHttpRequest.response
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractErrorDetail(e: any): string {
    if (e.data?.response?.detail) {
        return _toString(e.data.response.detail);
    }
    if (e.data?.response?.status?.detail) {
        if (e.data?.response?.status?.msg) {
            return `${_toString(e.data.response.status.msg)}.\n ${_toString(e.data?.response?.status?.detail)}`;
        }
        return _toString(e.data.response.status.detail);
    }
    if (e.data?.response?.status?.msg) {
        return _toString(e.data?.response?.status?.msg);
    }
    if (e.data?.response?.message) {
        return _toString(e.data.response.message);
    }
    if (e.data?.status) {
        return _toString(e.data.status);
    }
    if (e.data?.message) {
        return _toString(e.data.message);
    }
    return e.status || 'ERROR';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractS3Error(e: any): string {
    // EXAMPLE
    // {
    //     "name":"AccessDenied",
    //     "$fault":"client",
    //     "$metadata":{
    //         "httpStatusCode":403,
    //         "requestId":"18820979B62D95F5",
    //         "extendedRequestId":"dd9025bab4ad464b049177c95eb6ebf374d3b3fd1af9251148b658df7ac2e3e8",
    //         "attempts":1,
    //         "totalRetryDelay":0
    //     },
    //     "Code":"AccessDenied",
    //     "Key":"import.csv",
    //     "BucketName":"account5",
    //     "Resource":"/account5/import.csv",
    //     "RequestId":"18820979B62D95F5",
    //     "HostId":"dd9025bab4ad464b049177c95eb6ebf374d3b3fd1af9251148b658df7ac2e3e8",
    //     "message":"Access Denied."
    // }
    if (e.message) {
        return e.message;
    }
    if (e.name) {
        return `${e.name} httpStatusCode=${e.$metadata?.httpStatusCode}`;
    }
    return 'Unknown error';
}

/**
 * Функция форматирования байтов к их сжатому строковому виду.
 *
 * @param bytes значение в байтах.
 * @param {number=} [decimals=2] - количество значимых цифр после запятой.
 * @param {'ru' | 'en'} [lang='ru'] - язык ответа.
 */
export function formatBytes(bytes: number, decimals: number | undefined = 2, lang: 'ru' | 'en' = 'ru'): string {
    if (!+bytes) return '0 ' + (lang === 'ru' ? 'Байт' : 'Bytes');

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizesEn = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const sizesRu = ['б', 'Кб', 'Мб', 'Гб', 'Тб', 'Пб', 'Эб', 'Зб', 'Йб'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${lang === 'ru' ? sizesRu[i] : sizesEn[i]}`;
}

/**
 * Функция форматирования битрейта к его сжатому строковому виду.
 *
 * @param bitrate значение в байтах.
 * @param {number=} [decimals=1] - количество значимых цифр после запятой.
 * @param {'ru' | 'en'} [lang='ru'] - язык ответа.
 */
export function formatBitrate(bitrate: number, decimals: number | undefined = 1, lang: 'ru' | 'en' = 'ru'): string {
    if (!+bitrate) return '0 ' + (lang === 'ru' ? 'Байт/c' : 'Bytes/s');

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizesEn = ['Bytes/s', 'Kb/s', 'Mb/s', 'Gb/s', 'Tb/s', 'Pb/s', 'Eb/s', 'Zb/s', 'Yb/s'];
    const sizesRu = ['б/с', 'Кб/с', 'Мб/с', 'Гб/с', 'Тб/с', 'Пб/с', 'Эб/с', 'Зб/с', 'Йб/с'];

    const i = Math.floor(Math.log(bitrate) / Math.log(k));

    return `${parseFloat((bitrate / Math.pow(k, i)).toFixed(dm))} ${lang === 'ru' ? sizesRu[i] : sizesEn[i]}`;
}

export function formatDuration(minutes: number, lang: 'ru' | 'en' = 'ru'): string {
    if (!+minutes) return '0';

    const realmin = ('0' + minutes % 60).slice(-2);
    const hours = Math.floor(minutes / 60);
    const HOURS = lang === 'ru' ? 'ч' : 'h';
    const MINUTES = lang === 'ru' ? 'мин' : 'min';

    return `${hours > 0 ? hours + HOURS : ''} ${realmin}${MINUTES}`;
}

const a: Record<string, string> = { ' ': '_', 'Ё': 'YO', 'Й': 'I', 'Ц': 'TS', 'У': 'U', 'К': 'K', 'Е': 'E', 'Н': 'N', 'Г': 'G', 'Ш': 'SH', 'Щ': 'SCH', 'З': 'Z', 'Х': 'H', 'Ъ': '\'', 'ё': 'yo', 'й': 'i', 'ц': 'ts', 'у': 'u', 'к': 'k', 'е': 'e', 'н': 'n', 'г': 'g', 'ш': 'sh', 'щ': 'sch', 'з': 'z', 'х': 'h', 'ъ': '\'', 'Ф': 'F', 'Ы': 'I', 'В': 'V', 'А': 'A', 'П': 'P', 'Р': 'R', 'О': 'O', 'Л': 'L', 'Д': 'D', 'Ж': 'ZH', 'Э': 'E', 'ф': 'f', 'ы': 'i', 'в': 'v', 'а': 'a', 'п': 'p', 'р': 'r', 'о': 'o', 'л': 'l', 'д': 'd', 'ж': 'zh', 'э': 'e', 'Я': 'Ya', 'Ч': 'CH', 'С': 'S', 'М': 'M', 'И': 'I', 'Т': 'T', 'Ь': '\'', 'Б': 'B', 'Ю': 'YU', 'я': 'ya', 'ч': 'ch', 'с': 's', 'м': 'm', 'и': 'i', 'т': 't', 'ь': '\'', 'б': 'b', 'ю': 'yu' };

export function transliterate(word: string) {
    return word.split('').map(function (char) {
        return a[char] || char;
    }).join('');
}

export function translit1(src: string) {
    const capitalLetters: Record<string, string> = {
        'А': 'A',
        'Б': 'B',
        'В': 'V',
        'Г': 'G',
        'Д': 'D',
        'Е': 'E',
        'Ё': 'E',
        'Ж': 'Zh',
        'З': 'Z',
        'И': 'I',
        'Й': 'Y',
        'К': 'K',
        'Л': 'L',
        'М': 'M',
        'Н': 'N',
        'О': 'O',
        'П': 'P',
        'Р': 'R',
        'С': 'S',
        'Т': 'T',
        'У': 'U',
        'Ф': 'F',
        'Х': 'H',
        'Ц': 'Ts',
        'Ч': 'Ch',
        'Ш': 'Sh',
        'Щ': 'Sch',
        'Ъ': '',
        'Ы': 'Y',
        'Ь': '',
        'Э': 'E',
        'Ю': 'Yu',
        'Я': 'Ya',
        ' ': '-',
    }

    const lowerLetters: Record<string, string> = {
        'а': 'a',
        'б': 'b',
        'в': 'v',
        'г': 'g',
        'д': 'd',
        'е': 'e',
        'ё': 'e',
        'ж': 'zh',
        'з': 'z',
        'и': 'i',
        'й': 'y',
        'к': 'k',
        'л': 'l',
        'м': 'm',
        'н': 'n',
        'о': 'o',
        'п': 'p',
        'р': 'r',
        'с': 's',
        'т': 't',
        'у': 'u',
        'ф': 'f',
        'х': 'h',
        'ц': 'ts',
        'ч': 'ch',
        'ш': 'sh',
        'щ': 'sch',
        'ъ': '',
        'ы': 'y',
        'ь': '',
        'э': 'e',
        'ю': 'yu',
        'я': 'ya'
    }

    let translitString = '';

    Array.from(src).forEach((char) => {
        if (lowerLetters[char]) {
            char = lowerLetters[char]
        } else if (capitalLetters[char]) {
            char = capitalLetters[char]
        } else if (!/^[a-z0-9]+$/i.test(char)) {
            char = ''
        }
        translitString += char.toLowerCase()
    })

    return translitString
}

export function ymHit(url?: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).ym(99058005, 'hit', url);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).ym(99048680, 'hit', url);
}

export function ymGoal(target?: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).ym(100032604, 'reachGoal', target);
}

export async function getCrc32(file: File | Blob | ArrayBuffer) {
    let buf;
    if (file instanceof File || file instanceof Blob) {
        const content = await file.arrayBuffer()
        buf = Buffer.from(content);
    } else {
        buf = file;
    }
    const crc = crc32buf(new Uint8Array(buf));
    const strCrc = (crc >>> 0).toString(16).padStart(8, '0');
    return strCrc;
}

export function parseDate(v: string | null | undefined) {
    if (v) {
        try {
            return new Date(v);
        } catch {
            return undefined;
        }
    }
    return undefined;
}

export function toMplatDate(date?: string | null, onlyDate?: boolean) {
    try {
        if (date) {
            const d = new Date(date);
            if (onlyDate) return d.toLocaleDateString();
            return d.toLocaleString();
        }
        return '---';
    } catch {
        if (onlyDate) return date?.replace('T', ' ').split('.')[0]?.substring(0, 10) || '---';
        return date?.replace('T', ' ').split('.')[0]?.substring(0, 16) || '---';
    }
}

export function toMplatTime(date?: string | null) {
    try {
        if (date) {
            const d = new Date(date);
            return d.toLocaleTimeString();
        }
        return '---';
    } catch {
        return date?.replace('T', ' ').split('.')[0]?.substring(11, 19) || '---';
    }
}

export function toMplatBackendDate(date?: Date, onlyDate?: boolean) {
    try {
        if (date) {
            const d = new Date(date);
            let textDate = '';
            textDate += d.getFullYear();
            textDate += '-';
            textDate += ('0' + (d.getMonth() + 1)).slice(-2);
            textDate += '-';
            textDate += ('0' + d.getDate()).slice(-2);
            if (onlyDate) return textDate;
            textDate += ' ';
            textDate += ('0' + d.getHours()).slice(-2);
            textDate += ':';
            textDate += ('0' + (d.getMinutes())).slice(-2);
            textDate += ':';
            textDate += ('0' + d.getSeconds()).slice(-2);
            return textDate;
        }
        return undefined;
    } catch {
        return undefined;
    }
}

export function toMplatValidity(validity: Validity | null) {
    try {
        if (validity) {
            const s = validity.lower;
            const e = validity.upper;
            if (s) {
                if (e) {
                    return `${toMplatDate(s, true)}–${toMplatDate(e, true)}`;
                }
                return `${toMplatDate(s, true)} - ∞`;
            }
        }
        return '∞';
    } catch {
        return '---';
    }
}

type KeyValue = number | string | symbol;

export function groupBy<T, K extends KeyValue>(array: T[], fn: (value: T) => K) {
    return array.reduce((result, item) => {
        const key = fn(item);
        if (!result[key]) result[key] = [];
        result[key].push(item);
        return result;
    }, {} as Record<K, T[]>);
}

export function isMedialogistika(name: string) {
    if (
        name === t('tv:MEDIALOGISTIC')
        || name === 'Medialogistic'
    ) {
        return true;
    }
    return false;
}

export const useIsSafari = () => {
    const [isSafari, setIsSafari] = useState(false);

    useEffect(() => {
        // Check if window is defined (for SSR compatibility)
        if (typeof window !== 'undefined') {
            const userAgent = window.navigator.userAgent.toLowerCase();
            // Detect Safari: contains 'safari' but not 'chrome'
            const safari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
            setIsSafari(safari);
        }
    }, []);

    return isSafari;
};
